import { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

export default function Navigation() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  // Close mobile menu on Escape
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setIsMenuOpen(false);
    };
    if (isMenuOpen) {
      window.addEventListener('keydown', handleKey);
      return () => window.removeEventListener('keydown', handleKey);
    }
  }, [isMenuOpen]);

  const handleContactClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    if (location.pathname === '/') {
      const contactSection = document.getElementById('re-contact');
      if (contactSection) {
        contactSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    } else {
      navigate('/#re-contact');
      setTimeout(() => {
        const contactSection = document.getElementById('re-contact');
        if (contactSection) {
          contactSection.scrollIntoView({ behavior: 'auto', block: 'start' });
        }
      }, 100);
    }
    setIsMenuOpen(false);
  };

  return (
    <header className="fixed top-0 left-0 right-0 z-50 flex justify-center pt-3 px-4">
      <div className="mx-auto flex w-full items-center max-w-[960px] rounded-full bg-white/85 border border-black/[0.06] shadow-[0_1px_3px_rgba(0,0,0,0.06)] pl-5 pr-3 py-2.5 backdrop-blur-xl transition-all duration-500">
        {/* Logo mark + Brand name */}
        <a href="/" className="shrink-0 flex items-center gap-2.5 select-none">
          <img
            src="https://storage.readdy-site.link/project_files/e0ccb82e-1efa-45a2-81a6-920d70939725/41d16da0-7cab-4aed-8f24-f1b4deab9fd4_compressed_logo2b.webp"
            alt="Pragmatic AI logo"
            className="w-7 h-7 object-contain"
          />
          <span className="text-gray-900 font-semibold text-[15px] tracking-tight">
            Pragmatic AI
          </span>
        </a>

        {/* Desktop Navigation */}
        <nav className="hidden md:flex items-center gap-8 ml-10">
          <a
            href="/pricing"
            className="text-gray-500 hover:text-gray-900 transition-colors text-[15px] font-medium"
          >
            Pricing
          </a>
          <a
            href="/faq"
            className="text-gray-500 hover:text-gray-900 transition-colors text-[15px] font-medium"
          >
            FAQs
          </a>
        </nav>

        {/* Desktop CTA */}
        <div className="ml-auto hidden md:flex items-center gap-2.5">
          <a
            href="#contact"
            onClick={handleContactClick}
            className="rounded-full bg-gray-900 hover:bg-black px-5 py-2 text-sm font-medium text-white transition-colors whitespace-nowrap cursor-pointer"
          >
            Contact us
          </a>
        </div>

        {/* Mobile hamburger */}
        <button
          type="button"
          onClick={() => setIsMenuOpen(!isMenuOpen)}
          className="ml-auto md:hidden flex items-center justify-center w-10 h-10 rounded-full hover:bg-black/5 transition-colors"
          aria-label="Toggle menu"
        >
          <i className={`ri-${isMenuOpen ? 'close' : 'menu'}-line text-xl text-gray-700`}></i>
        </button>
      </div>

      {/* Mobile menu dropdown */}
      {isMenuOpen && (
        <div className="md:hidden absolute top-full left-4 right-4 mt-2 bg-white/95 backdrop-blur-xl rounded-2xl border border-black/[0.06] shadow-lg p-2">
          <nav className="flex flex-col">
            <a
              href="/pricing"
              onClick={() => setIsMenuOpen(false)}
              className="text-gray-700 hover:text-gray-900 hover:bg-gray-50 px-4 py-3 rounded-xl text-[15px] font-medium transition-colors"
            >
              Pricing
            </a>
            <a
              href="/faq"
              onClick={() => setIsMenuOpen(false)}
              className="text-gray-700 hover:text-gray-900 hover:bg-gray-50 px-4 py-3 rounded-xl text-[15px] font-medium transition-colors"
            >
              FAQs
            </a>
            <a
              href="#contact"
              onClick={handleContactClick}
              className="text-gray-700 hover:text-gray-900 hover:bg-gray-50 px-4 py-3 rounded-xl text-[15px] font-medium transition-colors"
            >
              Contact us
            </a>
          </nav>
        </div>
      )}
    </header>
  );
}